import { useState, useEffect, useLayoutEffect } from "react";
import { BASE_SERIES, KNOWN_SERIES, isLightTheme } from "../lib/colors.js";
/** Owns the `theme` preference (persisted to localStorage with the legacy duskMode
 *  migration) and applies it by setting `data-theme` on the document root. The theme
 *  palettes live entirely in CSS custom properties — GlobalStyles emits one
 *  `:root[data-theme=…]{--c-*:…}` block per theme, and inline styles read the frozen
 *  `C` map of `var(--c-*)` references — so activating a theme is a single attribute
 *  write the browser resolves with no module mutation and no forced re-render (the
 *  old `Object.assign(C, t)` render-phase mutation + its stale-until-hover hazard are
 *  gone). A light-theme series-color remap over BASE_SERIES/KNOWN_SERIES stays JS-side
 *  because those colors feed SVG stroke attributes + chart computation, not CSS vars.
 *  @returns {[string, import('react').Dispatch<import('react').SetStateAction<string>>]} */
export function useTheme() {
  // One-time force-to-Dark migration (new-default rollout). The FIRST time a browser
  // loads a build with this migration, override any prior stored theme to "dev" (the
  // "Dark" theme) — so EVERYONE lands on Dark once, regardless of what they had
  // selected before. Guarded by a versioned marker so it runs exactly once per
  // browser; runs BEFORE the theme useState below reads sooth-theme (hooks run
  // top-down), so the very first paint is already Dark. After it runs, the user's own
  // theme selection persists normally (bump the marker version to force again in a
  // future rollout). Idempotent, so a StrictMode double-invoke is harmless.
  useState(() => {
    try {
      if (localStorage.getItem("sooth-theme-dark-default-migrated") !== "1") {
        localStorage.setItem("sooth-theme", "dev");
        localStorage.setItem("sooth-theme-dark-default-migrated", "1");
      }
    } catch (_e) {
      /* localStorage unavailable */
    }
    return 0;
  });
  const [theme, setTheme] = useState(() => {
    try {
      const t = localStorage.getItem("sooth-theme");
      // `dark` is no longer offered — the third Settings option is `base`
      // ("Bichromatic"). Anyone still holding it is migrated rather than left on a theme
      // with no selected cell: every cell's tabIndex is -1 unless selected, so an
      // unmatched id makes the radiogroup unreachable by keyboard.
      if (t === "dark") return "base";
      if (t && ["dusk", "base", "dev"].includes(t)) return t;
      if (localStorage.getItem("sooth-duskMode") === "true") return "dusk";
      return "dev";
    } catch (_e) {
      return "dev";
    }
  });
  // Persist the preference + remap series colors (real side effects → effect).
  // The light-theme series remap stays here (NOT in the render-phase memo below):
  // react-compiler forbids the direct BASE_SERIES/KNOWN_SERIES member mutations
  // during render, and unlike C-driven UI text, the time-series colors aren't the
  // stale-until-hover case (their chart re-renders on its own data cadence).
  useEffect(() => {
    try {
      localStorage.setItem("sooth-theme", theme);
    } catch (_e) {
      /* localStorage unavailable */
    }
    const lightSeriesColors = {
      "#F7CE46": "#b89b00",
      "#66ccff": "#2277aa",
      "#aaddff": "#3366aa",
      "#88ff88": "#228822",
      "#99ccff": "#3366aa",
      "#ff99cc": "#cc3366",
      "#ddcc66": "#998822",
      "#eecc44": "#aa8800",
      "#f7931a": "#cc6600",
      "#ffaa44": "#cc7700",
      "#ff8844": "#cc5500",
      "#cc8844": "#995522",
    };
    const isLight = isLightTheme(theme);
    const remap = isLight ? (c) => lightSeriesColors[c] || c : (c) => c;
    Object.keys(BASE_SERIES).forEach((k) => {
      BASE_SERIES[k]._origColor = BASE_SERIES[k]._origColor || BASE_SERIES[k].color;
      BASE_SERIES[k].color = isLight ? remap(BASE_SERIES[k]._origColor) : BASE_SERIES[k]._origColor;
    });
    Object.keys(KNOWN_SERIES).forEach((k) => {
      KNOWN_SERIES[k]._origColor = KNOWN_SERIES[k]._origColor || KNOWN_SERIES[k].color;
      KNOWN_SERIES[k].color = isLight
        ? remap(KNOWN_SERIES[k]._origColor)
        : KNOWN_SERIES[k]._origColor;
    });
  }, [theme]);
  // Activate the theme by writing `data-theme` on the document root. GlobalStyles
  // emits a `:root[data-theme=…]{--c-*:…}` block per theme, so this single
  // attribute write swaps the whole `--c-*` palette; every inline `style={{ color:
  // C.x }}` (now a `var(--c-x)` reference) and every `var(--c-x)` CSS rule repaints
  // with no module mutation and no forced re-render. Runs in a LAYOUT effect so the
  // DOM write lands BEFORE paint (no one-frame flash of the previous theme) and
  // never happens during render (safe under concurrent-mode aborted/retried
  // renders). Guarded for non-DOM environments (SSR / a node test runner that
  // flushes effects). Idempotent → safe under a StrictMode double-invoke.
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  return [theme, setTheme];
}
